import { useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Edit, Trash2, Search, HelpCircle, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ACCESS_TOKEN } from "@/constants";

import AdminModal from "@/components/modal/AdminModal";
import DeleteConfirmDialog from "@/components/modal/DeleteConfirmDialog";

const API_URL = import.meta.env.VITE_API_URL;

function ManageUsers() {
  const [searchTerm, setSearchTerm] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [openGuide, setOpenGuide] = useState(false);

  const getToken = () =>
    localStorage.getItem(ACCESS_TOKEN) || sessionStorage.getItem(ACCESS_TOKEN);

  const fetchJSON = async (url, options = {}) => {
    const token = getToken();

    const res = await fetch(url, {
      method: options.method || "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
        ...(options.headers || {}),
      },
      body: options.body,
    });

    const text = await res.text().catch(() => "");
    if (!res.ok) {
      throw new Error(
        `Request failed (${res.status}): ${text || res.statusText}`,
      );
    }

    try {
      return text ? JSON.parse(text) : null;
    } catch {
      return null;
    }
  };

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ["admin-users-page"],
    queryFn: async () => {
      const [usersRes, profileRes] = await Promise.all([
        fetchJSON(`${API_URL}/api/admin/get_users`),
        fetchJSON(`${API_URL}/api/admin/profile`),
      ]);

      const usersList =
        (usersRes && usersRes.users) ||
        (Array.isArray(usersRes) ? usersRes : []);

      const currentId = (profileRes && profileRes.id) || null;
      const currentRole = (profileRes && profileRes.role) || "";

      return {
        users: usersList,
        currentUserId: currentId,
        currentUserRole: currentRole,
      };
    },
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
    placeholderData: {
      users: [],
      currentUserId: null,
      currentUserRole: "",
    },
    initialData: {
      users: [],
      currentUserId: null,
      currentUserRole: "",
    },
  });

  const admin = (data && data.users) || [];
  const currentUserId = (data && data.currentUserId) || null;
  const currentUserRole = (data && data.currentUserRole) || "";
  const loadingUsers = (isLoading || isFetching) && admin.length === 0;
  const usersError = error ? error.message || "Failed to load users" : "";

  const filteredAdmins = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return admin.filter((acc) => {
      return (
        (acc.name || "").toLowerCase().includes(term) ||
        (acc.username || "").toLowerCase().includes(term) ||
        (acc.email || "").toLowerCase().includes(term) ||
        (acc.role || "").toLowerCase().includes(term) ||
        (acc.status || "").toLowerCase().includes(term)
      );
    });
  }, [admin, searchTerm]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpenGuide(false);
    };

    if (openGuide) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openGuide]);

  // Add new admin
  const handleAddNew = () => {
    setSelectedAdmin(null);
    setOpenModal(true);
  };

  // Edit admin
  const handleEdit = (admin) => {
    setSelectedAdmin(admin);
    setOpenModal(true);
  };

  // Delete admin
  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setOpenDeleteDialog(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    try {
      const renderEmptyState = (message, className = "text-gray-500") => (
        <div className={`text-center py-8 ${className}`}>{message}</div>
      );

      const renderActions = (acc) => {
        const isSelfAdmin =
          currentUserRole === "Admin" && currentUserId === acc.id;

        return (
          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              disabled={isSelfAdmin}
              onClick={isSelfAdmin ? undefined : () => handleEdit(acc)}
              className={`h-9 w-9 p-0 text-gray-600 hover:text-gray-700 hover:bg-gray-100 ${
                isSelfAdmin ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
              }`}>
              <Edit className="h-4 w-4" />
            </Button>

            <Button
              size="sm"
              variant="ghost"
              disabled={isSelfAdmin}
              onClick={
                isSelfAdmin ? undefined : () => handleDeleteClick(acc.id)
              }
              className={`h-9 w-9 p-0 text-red-600 hover:text-red-800 ${
                isSelfAdmin ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
              }`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      };
      const token =
        localStorage.getItem(ACCESS_TOKEN) ||
        sessionStorage.getItem(ACCESS_TOKEN);

      console.log("DELETE token key:", ACCESS_TOKEN);
      console.log("DELETE token value:", token);

      if (!token) {
        console.error("No access token found. You are not authenticated.");
        throw new Error("No access token found. Please log in again.");
      }

      const response = await fetch(
        `${API_URL}/api/admin/delete_user/${selectedId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        console.error("Delete API error:", data); // 👈 will show the Flask message
        throw new Error(data?.message || "Failed to delete user");
      }

      await refetchUsers(); // refresh list

      console.log("test");
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const renderEmptyState = (message, className = "text-gray-500") => (
    <div className={`text-center py-8 ${className}`}>{message}</div>
  );

  const renderActions = (acc) => {
    const isSelfAdmin = currentUserRole === "Admin" && currentUserId === acc.id;

    return (
      <div className="flex items-center justify-end gap-2">
        <Button
          size="sm"
          variant="ghost"
          disabled={isSelfAdmin}
          onClick={isSelfAdmin ? undefined : () => handleEdit(acc)}
          className={`h-9 w-9 p-0 text-gray-600 hover:text-gray-700 hover:bg-gray-100 ${
            isSelfAdmin ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
          }`}>
          <Edit className="h-4 w-4" />
        </Button>

        <Button
          size="sm"
          variant="ghost"
          disabled={isSelfAdmin}
          onClick={isSelfAdmin ? undefined : () => handleDeleteClick(acc.id)}
          className={`h-9 w-9 p-0 text-red-600 hover:text-red-800 ${
            isSelfAdmin ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
          }`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Search and Add section */}
      <Card className="shadow-md">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search users..."
                className="px-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button
              onClick={handleAddNew}
              className="w-full sm:w-auto bg-[#D2138C] hover:bg-[#B01076] rounded-lg text-white">
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table Section */}
      <Card className="shadow-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Users</CardTitle>

            <button
              type="button"
              onClick={() => setOpenGuide(true)}
              className="h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 flex items-center justify-center"
              aria-label="Open Users guide">
              <HelpCircle className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {/* ===== MOBILE: Card layout ===== */}
          <div className="md:hidden space-y-3">
            {loadingUsers
              ? renderEmptyState("Loading users...")
              : usersError
                ? renderEmptyState(usersError, "text-red-500")
                : filteredAdmins.length === 0
                  ? renderEmptyState("No users found")
                  : filteredAdmins.map((acc) => (
                      <div
                        key={acc.id}
                        className="rounded-lg border bg-white p-4 shadow-sm">
                        {/* Top: Name + Actions */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium text-gray-800 truncate">
                              {acc.name}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {acc.username}
                            </div>
                          </div>

                          {renderActions(acc)}
                        </div>

                        {/* Details */}
                        <div className="mt-3 space-y-2 text-sm text-gray-600">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-gray-500">Email</span>
                            <span className="text-right break-all">
                              {acc.email}
                            </span>
                          </div>

                          <div className="flex items-center justify-between gap-3">
                            <span className="text-gray-500">Phone</span>
                            <span className="text-right">
                              {acc.phone || "-"}
                            </span>
                          </div>

                          <div className="flex items-center justify-between gap-3">
                            <span className="text-gray-500">Role</span>
                            <Badge
                              variant="secondary"
                              className={
                                acc.role === "Owner"
                                  ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                                  : acc.role === "Admin"
                                    ? "bg-[#D2138C]/10 text-[#D2138C] hover:bg-[#D2138C]/20"
                                    : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                              }>
                              {acc.role}
                            </Badge>
                          </div>

                          <div className="flex items-center justify-between gap-3">
                            <span className="text-gray-500">Status</span>
                            <Badge
                              variant="default"
                              className={
                                acc.status === "Active"
                                  ? "bg-green-100 text-green-700 hover:bg-green-200"
                                  : "bg-red-100 text-red-700 hover:bg-red-200"
                              }>
                              {acc.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
          </div>

          {/* ===== DESKTOP: Keep your table layout ===== */}
          <div className="hidden md:block rounded-lg border overflow-x-auto">
            <Table className="min-w-[640px]">
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="text-center text-black">Name</TableHead>
                  <TableHead className="text-center text-black">
                    Username
                  </TableHead>
                  <TableHead className="text-center text-black">
                    Email
                  </TableHead>
                  <TableHead className="text-center text-black">
                    Phone
                  </TableHead>
                  <TableHead className="text-center text-black">Role</TableHead>
                  <TableHead className="text-center text-black">
                    Status
                  </TableHead>
                  <TableHead className="text-center text-black">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {loadingUsers ? (
                  <TableRow>
                    <TableCell
                      colSpan="7"
                      className="text-center text-gray-500 py-8">
                      Loading users...
                    </TableCell>
                  </TableRow>
                ) : usersError ? (
                  <TableRow>
                    <TableCell
                      colSpan="7"
                      className="text-center text-red-500 py-8">
                      {usersError}
                    </TableCell>
                  </TableRow>
                ) : filteredAdmins.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan="7"
                      className="text-center text-gray-500 py-8">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAdmins.map((acc) => {
                    const isSelfAdmin =
                      currentUserRole === "Admin" && currentUserId === acc.id;

                    return (
                      <TableRow
                        key={acc.id}
                        className="text-center text-gray-600">
                        <TableCell>{acc.name}</TableCell>
                        <TableCell>{acc.username}</TableCell>
                        <TableCell>{acc.email}</TableCell>
                        <TableCell>{acc.phone || "-"}</TableCell>

                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={
                              acc.role === "Owner"
                                ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                                : acc.role === "Admin"
                                  ? "bg-[#D2138C]/10 text-[#D2138C] hover:bg-[#D2138C]/20"
                                  : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                            }>
                            {acc.role}
                          </Badge>
                        </TableCell>

                        <TableCell>
                          <Badge
                            variant="default"
                            className={
                              acc.status === "Active"
                                ? "bg-green-100 text-green-700 hover:bg-green-200"
                                : "bg-red-100 text-red-700 hover:bg-red-200"
                            }>
                            {acc.status}
                          </Badge>
                        </TableCell>

                        <TableCell>
                          <div className="flex justify-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={isSelfAdmin}
                              onClick={
                                isSelfAdmin ? undefined : () => handleEdit(acc)
                              }
                              className={`h-9 w-9 p-0 text-gray-600 hover:text-gray-700 hover:bg-gray-100 ${
                                isSelfAdmin
                                  ? "opacity-40 cursor-not-allowed"
                                  : "cursor-pointer"
                              }`}>
                              <Edit className="h-4 w-4" />
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={isSelfAdmin}
                              onClick={
                                isSelfAdmin
                                  ? undefined
                                  : () => handleDeleteClick(acc.id)
                              }
                              className={`h-9 w-9 p-0 text-red-600 hover:text-red-800 ${
                                isSelfAdmin
                                  ? "opacity-40 cursor-not-allowed"
                                  : "cursor-pointer"
                              }`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      {/* Add or edit admin */}
      <AdminModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        admin={selectedAdmin}
        onSuccess={refetchUsers}
      />

      {/* Delete Confirm */}
      <DeleteConfirmDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Admin"
        description="Are you sure you want to delete this admin user? This action cannot be undone."
      />

      {openGuide && (
        <div className="fixed left-0 top-0 w-screen h-screen z-[9999] flex items-center justify-center">
          {/* Backdrop */}
          <button
            type="button"
            className="absolute left-0 top-0 w-screen h-screen bg-black/40"
            onClick={() => setOpenGuide(false)}
            aria-label="Close guide"
          />

          {/* Modal */}
          <div className="relative z-10 w-full max-w-3xl mx-4 rounded-2xl bg-white shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-base font-semibold text-gray-900">
                Users guide
              </h2>

              <button
                type="button"
                onClick={() => setOpenGuide(false)}
                className="h-9 w-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
                aria-label="Close">
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 text-sm text-gray-700 max-h-[65vh] overflow-y-auto">
              <div>
                <p className="font-semibold text-gray-900 mb-2">
                  What you can do here
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>View all users (Owner, Admin, SDA Owner).</li>
                  <li>
                    Search users by name, username, email, role, or status.
                  </li>
                  <li>Add a new user using the Add User button.</li>
                  <li>Edit an existing user using the pencil icon.</li>
                  <li>Delete a user using the trash icon (if allowed).</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-gray-900 mb-2">Search users</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Type in the search bar to filter the table instantly.</li>
                  <li>
                    You can search by name, username, email, role, and status.
                  </li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-gray-900 mb-2">Add a user</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Click Add User.</li>
                  <li>
                    Fill in Name, Username, Email, Phone, Password, Role, and
                    Status.
                  </li>
                  <li>Click Add to save.</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-gray-900 mb-2">Edit a user</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Click the pencil icon on the user row.</li>
                  <li>Update the fields you need.</li>
                  <li>Save changes to apply updates.</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-gray-900 mb-2">
                  Delete a user
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Click the trash icon on the user row.</li>
                  <li>Confirm deletion in the popup.</li>
                  <li>
                    You cannot Delete your own account, (Own account can be
                    deleted in the settings).
                  </li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end border-t px-6 py-4">
              <Button
                type="button"
                className="bg-[#D2138C] hover:bg-[#B01076] text-white rounded-lg"
                onClick={() => setOpenGuide(false)}>
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageUsers;
